import type { CacheConfig, CacheEntry } from '@/types/CategoryTypes';

/**
 * Sistema de cache inteligente para otimização de performance
 * 
 * Características:
 * - TTL (Time To Live) configurável por tipo de dados
 * - Limpeza automática de entradas expiradas
 * - Estratégias de invalidação inteligente
 * - Métricas de performance do cache
 * - Compressão de dados para economizar memória
 */
export class CacheManager {
  private static instance: CacheManager;
  private cache: Map<string, CacheEntry<any>> = new Map();
  private config: CacheConfig;
  private metrics = {
    hits: 0,
    misses: 0,
    evictions: 0,
    totalRequests: 0
  };

  private constructor(config?: Partial<CacheConfig>) {
    this.config = {
      defaultTTL: 5 * 60 * 1000, // 5 minutos
      maxSize: 100,
      cleanupInterval: 60 * 1000, // 1 minuto
      enableMetrics: true,
      enableCompression: false,
      ...config
    };

    // Iniciar limpeza automática
    this.startCleanupInterval();
  }

  /**
   * Singleton pattern
   */
  public static getInstance(config?: Partial<CacheConfig>): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager(config);
    }
    return CacheManager.instance;
  }

  /**
   * Armazena dados no cache com TTL específico
   */
  public set<T>(
    key: string, 
    data: T, 
    ttl?: number,
    tags?: string[]
  ): void {
    const now = Date.now();
    const expiresAt = now + (ttl || this.config.defaultTTL);

    // Verificar se precisa fazer limpeza por tamanho
    if (this.cache.size >= this.config.maxSize) {
      this.evictOldest();
    }

    const entry: CacheEntry<T> = {
      data: this.config.enableCompression ? this.compress(data) : data,
      timestamp: now,
      expiresAt,
      accessCount: 0,
      lastAccessed: now,
      tags: tags || [],
      compressed: this.config.enableCompression
    };

    this.cache.set(key, entry);
  }

  /**
   * Recupera dados do cache
   */
  public get<T>(key: string): T | null {
    this.updateMetrics('request');

    const entry = this.cache.get(key);
    if (!entry) {
      this.updateMetrics('miss');
      return null;
    }

    const now = Date.now();
    
    // Verificar se expirou
    if (now > entry.expiresAt) {
      this.cache.delete(key);
      this.updateMetrics('miss');
      return null;
    }

    // Atualizar estatísticas de acesso
    entry.accessCount++;
    entry.lastAccessed = now;

    this.updateMetrics('hit');

    // Descomprimir se necessário
    const data = entry.compressed ? this.decompress(entry.data) : entry.data;
    return data as T;
  }

  /**
   * Verifica se uma chave existe no cache e não expirou
   */
  public has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    const now = Date.now();
    if (now > entry.expiresAt) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Remove uma entrada específica do cache
   */
  public delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * Remove entradas por tag
   */
  public deleteByTag(tag: string): number {
    let deletedCount = 0;
    
    for (const [key, entry] of this.cache.entries()) {
      if (entry.tags.includes(tag)) {
        this.cache.delete(key);
        deletedCount++;
      }
    }

    return deletedCount;
  }

  /**
   * Remove entradas por padrão de chave
   */
  public deleteByPattern(pattern: string | RegExp): number {
    let deletedCount = 0;
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
    
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key);
        deletedCount++;
      }
    }

    return deletedCount;
  }

  /**
   * Limpa todo o cache
   */
  public clear(): void {
    this.cache.clear();
    this.resetMetrics();
  }

  /**
   * Invalida cache baseado em critérios específicos
   */
  public invalidate(criteria: {
    accountId?: string;
    monthlyPlanId?: string;
    categoryType?: string;
    olderThan?: number;
  }): number {
    let invalidatedCount = 0;
    const now = Date.now();

    for (const [key, entry] of this.cache.entries()) {
      let shouldInvalidate = false;

      // Verificar critérios de invalidação
      if (criteria.accountId && key.includes(criteria.accountId)) {
        shouldInvalidate = true;
      }
      
      if (criteria.monthlyPlanId && key.includes(criteria.monthlyPlanId)) {
        shouldInvalidate = true;
      }
      
      if (criteria.categoryType && key.includes(criteria.categoryType)) {
        shouldInvalidate = true;
      }
      
      if (criteria.olderThan && (now - entry.timestamp) > criteria.olderThan) {
        shouldInvalidate = true;
      }

      if (shouldInvalidate) {
        this.cache.delete(key);
        invalidatedCount++;
      }
    }

    return invalidatedCount;
  }

  /**
   * Obtém estatísticas do cache
   */
  public getStats() {
    const now = Date.now();
    let expiredCount = 0;
    let totalSize = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        expiredCount++;
      }
      totalSize += this.estimateSize(entry);
    }

    const hitRate = this.metrics.totalRequests > 0 
      ? (this.metrics.hits / this.metrics.totalRequests) * 100 
      : 0;

    return {
      size: this.cache.size,
      maxSize: this.config.maxSize,
      expiredEntries: expiredCount,
      estimatedMemoryUsage: totalSize,
      hitRate: Math.round(hitRate * 100) / 100,
      ...this.metrics
    };
  }

  /**
   * Obtém informações detalhadas sobre entradas do cache
   */
  public getEntryInfo(key: string) {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();
    return {
      key,
      size: this.estimateSize(entry),
      age: now - entry.timestamp,
      timeToExpire: entry.expiresAt - now,
      accessCount: entry.accessCount,
      lastAccessed: entry.lastAccessed,
      tags: entry.tags,
      compressed: entry.compressed,
      expired: now > entry.expiresAt
    };
  }

  /**
   * Lista todas as chaves do cache
   */
  public getKeys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Pré-aquece o cache com dados específicos
   */
  public async warmup(
    dataLoader: (key: string) => Promise<any>,
    keys: string[],
    ttl?: number
  ): Promise<void> {
    const promises = keys.map(async (key) => {
      try {
        const data = await dataLoader(key);
        this.set(key, data, ttl);
      } catch (error) {
        console.warn(`Failed to warmup cache for key: ${key}`, error);
      }
    });

    await Promise.allSettled(promises);
  }

  /**
   * Inicia o intervalo de limpeza automática
   */
  private startCleanupInterval(): void {
    setInterval(() => {
      this.cleanup();
    }, this.config.cleanupInterval);
  }

  /**
   * Remove entradas expiradas
   */
  private cleanup(): void {
    const now = Date.now();
    let cleanedCount = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        this.cache.delete(key);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
      console.debug(`Cache cleanup: removed ${cleanedCount} expired entries`);
    }
  }

  /**
   * Remove a entrada mais antiga quando o cache está cheio
   */
  private evictOldest(): void {
    let oldestKey: string | null = null;
    let oldestTime = Date.now();

    for (const [key, entry] of this.cache.entries()) {
      if (entry.lastAccessed < oldestTime) {
        oldestTime = entry.lastAccessed;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
      this.updateMetrics('eviction');
    }
  }

  /**
   * Comprime dados para economizar memória
   */
  private compress<T>(data: T): string {
    try {
      return btoa(JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to compress cache data:', error);
      return JSON.stringify(data);
    }
  }

  /**
   * Descomprime dados
   */
  private decompress<T>(compressedData: string): T {
    try {
      return JSON.parse(atob(compressedData));
    } catch (error) {
      console.warn('Failed to decompress cache data:', error);
      return JSON.parse(compressedData);
    }
  }

  /**
   * Estima o tamanho de uma entrada do cache
   */
  private estimateSize(entry: CacheEntry<any>): number {
    try {
      const dataSize = JSON.stringify(entry.data).length;
      const metadataSize = JSON.stringify({
        timestamp: entry.timestamp,
        expiresAt: entry.expiresAt,
        accessCount: entry.accessCount,
        lastAccessed: entry.lastAccessed,
        tags: entry.tags
      }).length;
      
      return dataSize + metadataSize;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Atualiza métricas do cache
   */
  private updateMetrics(type: 'hit' | 'miss' | 'eviction' | 'request'): void {
    if (!this.config.enableMetrics) return;

    switch (type) {
      case 'hit':
        this.metrics.hits++;
        break;
      case 'miss':
        this.metrics.misses++;
        break;
      case 'eviction':
        this.metrics.evictions++;
        break;
      case 'request':
        this.metrics.totalRequests++;
        break;
    }
  }

  /**
   * Reseta métricas do cache
   */
  private resetMetrics(): void {
    this.metrics = {
      hits: 0,
      misses: 0,
      evictions: 0,
      totalRequests: 0
    };
  }
}

// Exportar instância singleton configurada para categorias
export const categoryCache = CacheManager.getInstance({
  defaultTTL: 5 * 60 * 1000, // 5 minutos para dados de categoria
  maxSize: 50, // Limite menor para dados específicos
  cleanupInterval: 2 * 60 * 1000, // Limpeza a cada 2 minutos
  enableMetrics: true,
  enableCompression: true // Habilitar compressão para economizar memória
});
